import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Stack, Divider, Chip, CircularProgress, Button } from '@mui/material';
import { Upgrade as UpgradeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, AuthUser, BillingMeResponse } from '../../services/api';

// No hardcoded plans; fetched from backend

export function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthlyScans, setMonthlyScans] = useState<number>(0);
  const [billing, setBilling] = useState<BillingMeResponse | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setError(null);
        setLoading(true);
        const u = await apiClient.getCurrentUser();
        if (!isMounted) return;
        setUser(u);

        // Fetch billing (plan + usage) from backend
        const b = await apiClient.getMyBilling();
        if (!isMounted) return;
        setBilling(b);
        setMonthlyScans(b.usage?.usedScans ?? 0);
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.response?.data?.message || e.message || 'Failed to load profile');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const { planName, includedScans, remainingScans } = useMemo(() => {
    const plan = billing?.plan;
    const usage = billing?.usage;
    const included = plan?.includedScans ?? 0;
    const remaining = usage?.remainingScans ?? Math.max(0, included - (monthlyScans || 0));
    return { planName: plan?.name || '-', includedScans: included, remainingScans: remaining };
  }, [billing, monthlyScans]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Profile</Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Account</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} divider={<Divider flexItem orientation="vertical" />}> 
              <Box>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{user?.name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{user?.email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip size="small" color={user?.active ? 'success' : 'default'} label={user?.active ? 'Active' : 'Inactive'} />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Plan & Usage</Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<UpgradeIcon />}
                onClick={() => navigate('/pricing')}
              >
                Upgrade Plan
              </Button>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} divider={<Divider flexItem orientation="vertical" />}> 
              <Box>
                <Typography variant="body2" color="text.secondary">Plan</Typography>
                <Typography variant="body1">{planName}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Allowed scans (this month)</Typography>
                <Typography variant="body1">{billing?.usage?.allowedScans ?? includedScans}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Used scans (this month)</Typography>
                <Typography variant="body1">{billing?.usage?.usedScans ?? monthlyScans}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Remaining</Typography>
                <Typography variant="body1">{billing?.usage?.remainingScans ?? remainingScans}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}


